import { api } from './api';
import { documentService } from './documentService';
import { workspaceService } from './workspaceService';
import { ChatbotDocument } from '../types/chat';
import { documentStatusService } from './documentStatusService';
import { DocumentNode } from '../types/documents';

export interface ChatbotDocumentResponse {
  documents: ChatbotDocument[];
  total: number;
}

export interface CanAddToChatbotResponse {
  canBeAdded: boolean;
  reason?: string;
}

class ChatbotService {
  async getChatbotDocuments(): Promise<ChatbotDocument[]> {
    try {
      const response = await api.get('/index/chatbot/documents');
      return response.data.documents || [];
    } catch (error: any) {
      console.error('[chatbotService.getChatbotDocuments] Erro na requisição:', error);
      throw error;
    }
  }

  async runChatbotBatch(workspaceId: string): Promise<boolean> {
    try {
      const response = await api.post('/chatindoc/index/batch', {
        workspaceId,
        ...workspaceService.buildCustomerIdParams(),
      });

      if (response.data === false || response.data === 'false') {
        throw new Error('Falha ao enfileirar indexação em lote no chatbot');
      }

      return response.data === true || response.data === 'true';
    } catch (error: any) {
      console.error('[chatbotService.runChatbotBatch] Erro na requisição:', error);

      let errorMessage = 'Erro ao enfileirar indexação em lote no chatbot';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Sessão expirada. Faça login novamente.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Você não tem permissão para executar indexação em lote.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  }

    async addDocumentToChatbot(documentId: string, workspaceId: string): Promise<boolean> {
    try {
      const response = await api.post(`/chatindoc/index/${documentId}`, {
        workspaceId,
        ...documentService.buildCustomerIdParams(),
      });
      
      if (response.data === false || response.data === 'false') {
        throw new Error('Falha ao adicionar documento ao chatbot');
      }
      
      return response.data === true || response.data === 'true';
    } catch (error: any) {
      console.error('[chatbotService.addDocumentToChatbot] Erro na requisição:', error);
      
      let errorMessage = 'Erro ao adicionar documento ao chatbot';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Sessão expirada. Faça login novamente.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Documento não encontrado.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  async removeDocumentFromChatbot(documentId: string, workspaceId: string): Promise<boolean> {
    try {
      // Remover documento do chatbot do workspace informado (workspaceId no corpo do DELETE)
      const response = await api.delete(`/chatindoc/index/${documentId}`, {
        data: { workspaceId },
      });
      
      // Verificar se a operação foi bem-sucedida
      if (response.data === false || response.data === 'false') {
        throw new Error('Falha ao remover documento do chatbot');
      }
      
      // Retornar true se a operação foi bem-sucedida
      return response.data === true || response.data === 'true';
    } catch (error: any) {
      console.error('[chatbotService.removeDocumentFromChatbot] Erro na requisição:', error);
      
      // Extrair mensagem de erro específica da API
      let errorMessage = 'Erro ao remover documento do chatbot';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Sessão expirada. Faça login novamente.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Documento não encontrado.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  async addDocumentToChatbotBatch(documentId: string, workspaceIds: string[]): Promise<{ successCount: number; failedCount: number }> {
    try {
      const response = await api.post(`/chatindoc/index/${documentId}/batch`, {
        workspaceIds,
        ...documentService.buildCustomerIdParams(),
      });
      
      return {
        successCount: response.data.successCount || 0,
        failedCount: response.data.failedCount || 0,
      };
    } catch (error: any) {
      console.error('[chatbotService.addDocumentToChatbotBatch] Erro na requisição:', error);
      
      let errorMessage = 'Erro ao adicionar documento aos chatbots';
      
      if (error.response?.data?.message)
      {
        errorMessage = error.response.data.message;
      }
      else if (error.response?.status === 401)
      {
        errorMessage = 'Sessão expirada. Faça login novamente.';
      }
      else if (error.response?.status === 404)
      {
        errorMessage = 'Documento não encontrado.';
      }
      else if (error.message)
      {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  async removeDocumentFromChatbotBatch(documentId: string, workspaceIds: string[]): Promise<{ successCount: number; failedCount: number }> {
    try {
      const response = await api.delete(`/chatindoc/index/${documentId}/batch`, {
        data: {
          workspaceIds,
          ...documentService.buildCustomerIdParams(),
        },
      });
      
      return {
        successCount: response.data.successCount || 0,
        failedCount: response.data.failedCount || 0,
      };
    } catch (error: any) {
      console.error('[chatbotService.removeDocumentFromChatbotBatch] Erro na requisição:', error);
      
      let errorMessage = 'Erro ao remover documento dos chatbots';
      
      if (error.response?.data?.message)
      {
        errorMessage = error.response.data.message;
      }
      else if (error.response?.status === 401)
      {
        errorMessage = 'Sessão expirada. Faça login novamente.';
      }
      else if (error.response?.status === 404)
      {
        errorMessage = 'Documento não encontrado.';
      }
      else if (error.message)
      {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  async canDocumentBeAddedToChatbot(documentId: string): Promise<CanAddToChatbotResponse> {
    try {
      await documentStatusService.getDocumentStatus(documentId);

      return {
        canBeAdded: true,
        reason: 'Documento válido para adicionar ao chatbot'
      };
    } catch (error: any) {
      console.error('[chatbotService.canDocumentBeAddedToChatbot] Erro ao verificar status:', error);
      
      // Em caso de erro, retornar que não pode ser adicionado
      return {
        canBeAdded: false,
        reason: 'Erro ao verificar status do documento'
      };
    }
  }
}

export const chatbotService = new ChatbotService(); 